import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, Signal, ViewEncapsulation, computed, effect, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivityEntityName, ActivityKind, ActivityShareType } from '@jms/model';

import { ActivityContext, ActivityDisplayMode, ActivityFilter, ActivityFilterType, ActivityHost } from '../../model';
import { UiActivityUtility, resolveNewActivityKinds } from '../../utilities';

import { Command } from '@workbuddy/ui/model';
import { ViewOptionType, ViewOptionsComponent } from '@workbuddy/ui/components/view.options';
import { ViewOptionSchema, ViewOptionsBuilder } from '@workbuddy/ui/components/view.options/model';
import { BUTTON_COMPONENTS, ButtonSize } from '@workbuddy/ui/components/button';
import { SearchComponent } from '@workbuddy/ui/fields/search';

import { SignalInput } from '@workbuddy/utils/signals';
import { SelectionService } from '@workbuddy/ui/services';

import Enumerable from 'linq';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'activity-header',
  templateUrl: './activity.header.component.html',
  styleUrls: ['./activity.header.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,

    SearchComponent,

    ViewOptionsComponent,
    BUTTON_COMPONENTS,

  ],
  host: {
    '[class.sidebar]': '$display() === ActivityDisplayMode.SideBar',
    '[class.fullscreen]': '$display() === ActivityDisplayMode.Fullscreen || $isClaims()',
    '[class.claims]': '$isClaims()',
  }
})
export class ActivityHeaderComponent {

  @SignalInput() @Input('context') public $context: Signal<ActivityContext>;

  @Output() public add = new EventEmitter<ActivityKind>();
  @Output() public link = new EventEmitter<string[]>();
  @Output() public clear = new EventEmitter();


  protected newCommands = Enumerable
    .from([ActivityKind.Note, ActivityKind.Form, ActivityKind.Email, ActivityKind.SMS, ActivityKind.Integration, ActivityKind.Thread, ActivityKind.Phone])
    .toObject(x => x, kind => {
      return new Command({
        _id: kind,
        label: kind === ActivityKind.Phone ? "Call" : kind,
        iconLeft: `fa-regular ${UiActivityUtility.getKindIcon(UiActivityUtility.getActivityLike(kind))}`,
        iconLeftColor: UiActivityUtility.getKindColor(UiActivityUtility.getActivityLike(kind)),
        action: () => this.onRaiseAction(kind)
      });
    }) as { [key: string]: Command };

  protected $viewOptions = signal(null);
  protected $type = signal(ActivityFilterType.User);
  protected $search = signal(null);

  protected $buttonSize = computed(() => {
    const context = this.$context();
    return ButtonSize.Small;
    // return [ActivityDisplayMode.SideBar, ActivityDisplayMode.LocalClaims, ActivityDisplayMode.RemoteClaims].includes(context.display)
    //   ? ButtonSize.Small
    //   : ButtonSize.Medium;
  });

  protected $isClaims = computed(() => {
    const context = this.$context();
    return [ActivityHost.LocalClaims, ActivityHost.RemoteClaims].includes(context?.host);
  });

  protected $isRemoteClaims = computed(() => {
    const context = this.$context();
    return context?.host === ActivityHost.RemoteClaims;
  });

  protected $isPortal = computed(() => {
    const context = this.$context();
    return [ActivityHost.ContractorPortal, ActivityHost.ClientPortal].includes(context?.host);
  });

  protected $initialFilterType = computed(() => this.$context()?.initialFilterType);

  protected $types = computed(() => {
    const isPortal = this.$isPortal();
    const current = this.$type();
    if (isPortal) return [
      new Command({ _id: ActivityFilterType.All, label: 'All', isDefault: true, hidden: true, action: () => this.$type.set(ActivityFilterType.All) }),
    ];

    return [
      new Command({ _id: ActivityFilterType.All, label: 'All', isDefault: current === ActivityFilterType.All, action: () => this.$type.set(ActivityFilterType.All) }),
      new Command({ _id: ActivityFilterType.User, label: '', iconLeft: 'fa-regular fa-user', isDefault: current === ActivityFilterType.User, action: () => this.$type.set(ActivityFilterType.User) }),
      new Command({ _id: ActivityFilterType.System, label: '', iconLeft: 'icomoon icomoon-wb-logo', isDefault: current === ActivityFilterType.System, action: () => this.$type.set(ActivityFilterType.System) }),
    ];
  });


  protected $newCommands = computed(() => {

    const context = this.$context();
    if (!context) {
      return [];
    }

    return resolveNewActivityKinds(context.entity?.type, context.host).map(kind => this.newCommands[kind]);
  });

  protected $viewOptionsSchema = computed(() => {

    const commands = this.$newCommands();
    if (!commands) {
      return null;
    }

    const context = this.$context();

    const kinds = Enumerable.from(commands)
      .select((command: Command) => {
        return {
          type: ViewOptionType.Boolean,
          title: `${command?.label === ActivityKind.SMS ? command?.label : command?.label + 's'}`,
          dbName: command._id,
          defaultValue: true
        } as ViewOptionSchema
      }).orderBy(x => x.title).toArray();

    const sharing = {
      type: ViewOptionType.Dropdown,
      title: 'Shared With',
      dbName: 'sharing',
      items: [
        {
          _id: 'All',
          name: 'All',
        },
        {
          _id: ActivityShareType.Customer,
          name: 'Customer',
        },
        {
          _id: ActivityShareType.Field,
          name: 'Field',
        },
        {
          _id: ActivityShareType.NotShared,
          name: 'Not Shared',
        }
      ],
      defaultValue: 'All'
    } as ViewOptionSchema;

    const tags = {
      type: ViewOptionType.DropdownMultiple,
      title: 'Tags',
      dbName: 'tags',
      defaultValue: [],
      items: [],
    }

    if (context?.tagsForFilter) {
      tags.items.push(...context.tagsForFilter.map(x => ({ _id: x._id, name: x.name, color: x.color })));
    }

    let builder = ViewOptionsBuilder
      .new()
      .addSection({
        dbName: '',
        title: 'Show',
      })
      .addOptions(kinds)

    if (sharing?.items?.length) {
      builder = builder
        .addDivider()
        .addOption(sharing, UiActivityUtility.canShareFromContext(this.$context()))
    }

    builder.addDivider()
      .addOption(tags)

    return builder.finishSection()
      .build();
  });

  protected $display = computed(() => this.$context()?.display);

  protected $hasActiveViewFilters = computed(() => {
    const viewOptions = this.$viewOptions();
    const commands = this.$newCommands();

    if (!viewOptions || !commands?.length) {
      return false;
    }

    const hasExcludedKinds = commands.some((command: Command) => viewOptions?.[command._id] === false);
    const hasSharingFilter = !!viewOptions?.sharing && viewOptions.sharing !== 'All';
    const hasTagFilter = Array.isArray(viewOptions?.tags) && viewOptions.tags.length > 0;

    return hasExcludedKinds || hasSharingFilter || hasTagFilter;
  });

  protected $filter = computed(() => {

    const viewOptions = this.$viewOptions();
    const type = this.$type();
    const search = this.$search();

    return {
      search,
      type,
      excludeKinds: viewOptions
        ? Enumerable.from(Object.keys(viewOptions)).where(x => viewOptions[x] == false).toArray()
        : [],
      sharing: viewOptions?.sharing,
      tags: viewOptions?.tags ?? [],
    } as ActivityFilter;

  });

  protected $showLinkSelected = computed(() => this.$context()?.host === ActivityHost.LocalClaims);
  protected $showUnlinkSelected = computed(() => this.$context()?.host === ActivityHost.RemoteClaims);

  @Output() public filter = toObservable(this.$filter);

  protected selectionService = inject(SelectionService, { optional: true });

  protected readonly ActivityEntityName = ActivityEntityName;
  protected readonly ActivityKind = ActivityKind;
  protected readonly ActivityDisplayMode = ActivityDisplayMode;

  private _debounce$ = new Subject<any>();

  constructor() {
    this._debounce$.asObservable().pipe(
      debounceTime(200)
    )
      .subscribe(value => {
        this.$viewOptions.set(value)
      });

    effect(() => {
      const initial = this.$initialFilterType();
      if (initial) this.$type.set(initial);
    });

    effect(() => {
      const context = this.$context();
      const commands = this.$newCommands();
      if (context?.currentFilter) {

        const viewOptions = {};
        for (const command of commands) {
          viewOptions[command._id] = !context.currentFilter?.excludeKinds?.includes(command._id as ActivityKind);
        }

        viewOptions['sharing'] = context.currentFilter.sharing ?? 'All';
        viewOptions['tags'] = context.currentFilter.tags;

        this.$viewOptions.set(viewOptions);
      }
    });
  }

  public onRaiseAction(kind: ActivityKind) {
    this.add.emit(kind)
  }

  public onViewOptionsChanged(value: any) {
    this._debounce$.next(value);
  }
}
